import React from 'react';
import { Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons';
import {
	AccountingSystemProviders,
	AccountingSystemStrategies,
	Currencies,
	DATE_AND_TIME,
	FeatureFlags,
	InvoiceTemplates,
} from '@shared/utils/constants';
import { generateActivationUrl } from '@shared/utils/generateActivationUrl';
import { formatHumanReadable } from '@shared/utils/string';
import type { OrganizationProps } from '@api/models/organization';
import { formatDate } from '@client/utils/formatDate';
import { EditableDetailsItem } from '@client/components/EditableField';
import Button from '@client/components/Button';
import showSuccessNotification from '@client/utils/showSuccessNotification';

const getItems = (
	org: OrganizationProps,
): EditableDetailsItem<any>[] | null => {
	const activationLink = generateActivationUrl(org?.activationToken ?? '', window.location.origin);

	return [
		{
			key: 'name',
			label: 'Name',
			value: org.name,
			type: 'text',
			editable: true,
		},
		{
			key: 'providerIds',
			label: 'Provider ID(s)',
			value: org.providerIds,
			editable: true,
			type: 'list',
		},
		{
			key: 'featureFlags',
			label: 'Feature flags',
			value: org.featureFlags,
			editable: true,
			type: 'select',
			multiple: true,
			options: Object.values(FeatureFlags).map((value) => ({
				label: formatHumanReadable(value),
				value,
			})),
		},
		{
			key: 'activationLink',
			label: 'Activation link',
			value: activationLink == null ? '' : (
				<Typography.Text copyable={{ text: activationLink }}>
					<a
						target="_blank"
						rel="noopener noreferrer"
						href={activationLink}
					>
						Link url
					</a>
				</Typography.Text>
			),
		},
		{
			label: 'Company name',
			key: 'companyName',
			value: org.companyName,
			type: 'text',
			editable: true,
		},
		{
			label: 'Invoice Template',
			key: 'invoiceTemplate',
			value: org.invoiceTemplate,
			type: 'select',
			options: Object.values(InvoiceTemplates).map((t) => ({
				label: t,
				value: t,
			})),
			editable: true,
		},
		{
			label: 'Base Currency',
			key: 'baseCurrency',
			value: org.baseCurrency,
			type: 'select',
			options: Object.values(Currencies).map((t) => ({
				label: t,
				value: t,
			})),
			inputProps: {
				showSearch: true,
			},
			editable: true,
		},
		{
			label: 'Bunker Accounting Method',
			key: 'bunkerAccountingMethod',
			value: org.bunkerAccountingMethod,
			type: 'select',
			editable: true,
			options: [
				{
					label: 'FIFO',
					value: 'FIFO',
				},
				{
					label: 'LIFO',
					value: 'LIFO',
				},
			],
		},
		{
			label: 'Address',
			key: 'address',
			value: org.address,
			type: 'text',
			editable: true,
		},
		{
			label: 'City',
			key: 'city',
			value: org.city,
			type: 'text',
			editable: true,
		},
		{
			label: 'Country',
			key: 'country',
			value: org.country,
			type: 'text',
			editable: true,
		},
		{
			label: 'Use vessel company name on invoices',
			key: 'useVesselCompanyOnInvoice',
			value: org.useVesselCompanyOnInvoice?.toString(),
			type: 'select',
			options: [
				{ label: 'Yes', value: 'true' },
				{ label: 'No', value: 'false' },
			],
			editable: true,
		},
		{
			label: '[The Ship] Client ID',
			key: 'theShipClientId',
			value: org.theShipClientId,
			type: 'text',
			editable: true,
		},
		{
			label: '[The Ship] Client Secret',
			key: 'theShipClientSecret',
			value: org.theShipClientSecret,
			type: 'text',
			editable: true,
		},
		{
			label: '[The Ship] Company ID',
			key: 'theShipCompanyId',
			value: org.theShipCompanyId,
			type: 'text',
			editable: true,
		},
		{
			label: '[VesselIndex] Partner Username',
			key: 'vesselIndexPartnerUsername',
			value: org.vesselIndexPartnerUsername,
			type: 'text',
			editable: true,
		},
		{
			label: '[VesselIndex] Partner Secret',
			key: 'vesselIndexPartnerSecret',
			value: org.vesselIndexPartnerSecret,
			type: 'text',
			editable: true,
		},
		{
			label: '[DIS] API Key',
			key: 'disApiKey',
			value: org.disApiKey,
			type: 'text',
			editable: true,
		},
		{
			label: '[DIS] Principal ID',
			key: 'disPrincipalId',
			value: org.disPrincipalId,
			type: 'text',
			editable: true,
		},
		{
			label: '[Accounting] Client ID',
			key: 'accountingClientId',
			value: org.accountingClientId,
			type: 'text',
			editable: true,
		},
		{
			label: '[Accounting] Client Secret',
			key: 'accountingClientSecret',
			value: org.accountingClientSecret,
			type: 'text',
			editable: true,
		},
		{
			label: '[Accounting] Resource',
			key: 'accountingResource',
			value: org.accountingResource,
			type: 'text',
			editable: true,
		},
		{
			label: 'Accounting System Provider',
			key: 'accountingSystemProvider',
			value: org.accountingSystemProvider,
			type: 'select',
			options: [
				{ label: 'None', value: null },
				{ label: 'Codat', value: AccountingSystemProviders.CODAT },
				{ label: 'Navision', value: AccountingSystemProviders.NAVISION },
				{ label: 'Intacct', value: AccountingSystemProviders.INTACCT },
				{ label: 'Xero', value: AccountingSystemProviders.XERO },
				{ label: 'Twinfields', value: AccountingSystemProviders.TWINFIELDS },
				{ label: '365 F&O', value: AccountingSystemProviders['365fo'] },
				{ label: '365 BC', value: AccountingSystemProviders['365bc'] },
			],
			editable: true,
		},
		{
			label: 'Accounting System Strategy',
			key: 'accountingSystemStrategy',
			value: org.accountingSystemStrategy,
			type: 'select',
			options: [
				{ label: 'Global', value: AccountingSystemStrategies.GLOBAL },
				{ label: 'Voyage-based', value: AccountingSystemStrategies.VOYAGE },
				{ label: 'Vessel-based', value: AccountingSystemStrategies.VESSEL },
			],
			editable: true,
		},
		{
			label: '[Twinfields] Connect URL',
			key: 'twinFieldsConnectUrl',
			render: () => (
				<Button
					type="primary"
					onClick={async () => {
						const url = `
						https://login.twinfield.com/auth/authentication/connect/authorize?
						client_id=clearvoyage&
						response_type=code&
						scope=openid+twf.user+twf.organisation+twf.organisationUser+offline_access&
						redirect_uri=${process.env.REACT_APP_API_URL}/twinfields&
						state=${org?.id}&
						nonce=world
					`;
						await navigator.clipboard.writeText(url);
						showSuccessNotification('Copied');
					}}
					icon={(<FontAwesomeIcon icon={faCopy} />)}
				>
					Copy Twinfields connection URL
				</Button>
			),
		},
		{
			label: 'Root Accounting Company ID',
			key: 'rootAccountingCompanyId',
			value: org.rootAccountingCompanyId,
			type: 'text',
			editable: true,
		},
		{
			key: 'accountingCutoffDate',
			label: 'Accounting Cutoff Date',
			value: org.accountingCutoffDate,
			inputProps: {
				time: true,
				showTime: true,
			},
			type: 'date',
			editable: true,
			render: () => formatDate(org.accountingCutoffDate, DATE_AND_TIME),
		},
		{
			key: 'createdAt',
			label: 'Creation date',
			value: formatDate(org.createdAt, DATE_AND_TIME),
		},
		{
			key: 'updatedAt',
			label: 'Last updated',
			value: formatDate(org.updatedAt, DATE_AND_TIME),
		},
	];
};

export default getItems;
